import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";
import {
  BarChart3, Plus, X, Loader2, Trash2, Edit3, LayoutDashboard, PieChart, Activity,
  TrendingUp, Wallet, ShieldCheck, Users, Target, BookOpen, Check, Play,
} from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, PieChart as RePie, Pie, Cell, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  LineChart, Line,
} from "recharts";

const PALETTE = ["#D4AF37", "#3B82F6", "#22C55E", "#A855F7", "#EA580C", "#EC4899", "#06B6D4", "#84CC16", "#F59E0B", "#94A3B8"];
const CAT_ICON = { Engagement: Users, Proposals: TrendingUp, Operations: Target, Finance: Wallet, GRC: ShieldCheck, Knowledge: BookOpen };
const TABS = [
  { id: "overview", label: "Overview" },
  { id: "explore", label: "Explore" },
  { id: "dashboards", label: "Dashboards" },
];

export default function Analytics() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const setTab = (id) => setParams({ tab: id });
  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="analytics-page">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <BarChart3 className="w-3.5 h-3.5" style={{ color: "var(--gold-400)" }} strokeWidth={1.6} />
          <span className="label-eyebrow">// Business Intelligence &amp; Analytics</span>
        </div>
        <h1 className="font-display text-3xl text-white tracking-tight">Analytics</h1>
        <p className="text-slate-400 text-sm mt-1 max-w-2xl">
          18 cross-module metrics — engagement, proposals, operations, finance, GRC. Build custom dashboards and run
          them in one click.
        </p>
      </div>
      <div className="flex flex-wrap gap-1 border-b border-white/5" data-testid="analytics-tabs">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`analytics-tab-${t.id}`} onClick={() => setTab(t.id)}
                  className={`px-4 py-2.5 font-mono text-[11px] tracking-widest transition-all border-b-2 -mb-px ${
                    tab === t.id ? "border-[#D4AF37] text-white" : "border-transparent text-slate-400 hover:text-white"
                  }`}>{t.label.toUpperCase()}</button>
        ))}
      </div>
      {tab === "overview" && <Overview onExplore={() => setTab("explore")} />}
      {tab === "explore" && <Explore />}
      {tab === "dashboards" && <Dashboards />}
    </div>
  );
}

/* ─────────────────────────── OVERVIEW ─────────────────────────── */
function Overview({ onExplore }) {
  const [kpis, setKpis] = useState(null);
  useEffect(() => { api.get("/analytics/kpis").then((r) => setKpis(r.data)); }, []);
  if (!kpis) return <Loading />;
  return (
    <div className="space-y-5" data-testid="analytics-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Pipeline (GHS)" value={fmtCurrency(kpis.pipeline_ghs)} accent="#D4AF37" />
        <Kpi label="Won last 30d (GHS)" value={fmtCurrency(kpis.won_last_30d_ghs)} accent="#22C55E" />
        <Kpi label="Stakeholders" value={kpis.stakeholders} accent="#3B82F6" />
        <Kpi label="AR outstanding (GHS)" value={fmtCurrency(kpis.ar_outstanding_ghs)} accent="#EA580C" />
        <Kpi label="Open tasks" value={kpis.open_tasks} accent="#94A3B8" />
        <Kpi label="Overdue" value={kpis.overdue_tasks} accent={kpis.overdue_tasks > 0 ? "#EF4444" : "#94A3B8"} />
        <Kpi label="Open risks" value={kpis.open_risks} accent="#F59E0B" />
        <Kpi label="Critical risks" value={kpis.critical_risks} accent={kpis.critical_risks > 0 ? "#DC2626" : "#94A3B8"} />
      </div>
      <div className="nexus-glass-panel p-6 flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1">
          <div className="label-eyebrow mb-1">// Next step</div>
          <h3 className="font-display text-xl text-white">Explore 18 metrics or build a custom dashboard</h3>
          <p className="text-sm text-slate-400 mt-1">Cross-module intelligence across engagement, proposals, operations, finance and GRC.</p>
        </div>
        <button onClick={onExplore} data-testid="overview-explore-cta"
                className="text-[11px] font-mono tracking-widest px-4 py-2.5"
                style={{ background: "var(--gold-500)", color: "#050B14" }}>
          EXPLORE METRICS →
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────── EXPLORE ─────────────────────────── */
function Explore() {
  const [catalogue, setCatalogue] = useState(null);
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/analytics/metrics").then((r) => {
      setCatalogue(r.data);
      const first = r.data.categories[0]?.metrics[0]?.id;
      if (first) setSelected(first);
    });
  }, []);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    api.get(`/analytics/query/${selected}`).then((r) => setResult(r.data)).finally(() => setLoading(false));
  }, [selected]);

  if (!catalogue) return <Loading />;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="analytics-explore">
      <div className="lg:col-span-1 nexus-glass-panel p-4 max-h-[75vh] overflow-y-auto">
        <div className="label-eyebrow mb-3">// {catalogue.total} metrics</div>
        <div className="space-y-3">
          {catalogue.categories.map((c) => {
            const Icon = CAT_ICON[c.name] || BarChart3;
            return (
              <div key={c.name}>
                <div className="flex items-center gap-2 mb-1.5">
                  <Icon className="w-3 h-3" style={{ color: "var(--gold-400)" }} />
                  <span className="font-mono text-[10px] tracking-widest text-slate-300">{c.name.toUpperCase()}</span>
                </div>
                <div className="space-y-0.5">
                  {c.metrics.map((m) => (
                    <button key={m.id} onClick={() => setSelected(m.id)} data-testid={`metric-${m.id}`}
                            className={`w-full text-left px-2.5 py-2 text-[12px] transition ${
                              selected === m.id ? "bg-[#D4AF37]/10 text-white border-l-2 border-[#D4AF37]" : "text-slate-400 hover:text-white hover:bg-white/5"
                            }`}>
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="lg:col-span-2 nexus-glass-panel p-5" data-testid="metric-result">
        {loading || !result ? <Loading /> : <ChartWithHeader result={result} />}
      </div>
    </div>
  );
}

/* ─────────────────────────── DASHBOARDS ─────────────────────────── */
function Dashboards() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [active, setActive] = useState(null);
  const [editingConfig, setEditingConfig] = useState(null);
  const load = () => {
    setLoading(true);
    api.get("/analytics/dashboards").then((r) => setRows(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);
  const del = async (id) => {
    if (!confirm("Delete this dashboard?")) return;
    await api.delete(`/analytics/dashboards/${id}`);
    toast.success("Dashboard deleted");
    if (active?.id === id) setActive(null);
    load();
  };
  if (loading) return <Loading />;
  if (active) return <RunDashboard did={active.id} onBack={() => setActive(null)} onEdit={() => setEditingConfig(active)} onDelete={() => del(active.id)} />;

  return (
    <div className="space-y-4" data-testid="analytics-dashboards">
      <div className="flex items-center justify-between">
        <span className="label-eyebrow">// Saved dashboards · {rows.length}</span>
        <button onClick={() => setShowForm(true)} data-testid="dashboard-add"
                className="text-[11px] font-mono tracking-widest px-4 py-2 flex items-center gap-2"
                style={{ background: "var(--gold-500)", color: "#050B14" }}>
          <Plus className="w-3 h-3" /> NEW DASHBOARD
        </button>
      </div>
      {rows.length === 0 ? (
        <div className="nexus-card p-8 text-center text-sm text-slate-400">No dashboards yet. Create one to combine your favourite metrics.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map((d) => (
            <div key={d.id} className="nexus-card p-4 cursor-pointer hover:border-[#D4AF37]/40 transition group"
                 onClick={() => setActive(d)} data-testid={`dashboard-${d.id}`}>
              <div className="flex items-center gap-2 mb-2">
                <LayoutDashboard className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.6} />
                <h3 className="font-display text-base text-white flex-1 truncate">{d.name}</h3>
              </div>
              {d.description && <p className="text-[12px] text-slate-400 leading-snug line-clamp-2">{d.description}</p>}
              <div className="flex items-center justify-between mt-3">
                <span className="font-mono text-[10px] tracking-widest text-slate-500">
                  {(d.widgets || []).length} WIDGETS
                </span>
                <button onClick={(e) => { e.stopPropagation(); del(d.id); }}
                        className="p-1.5 text-slate-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <AnimatePresence>
        {(showForm || editingConfig) && <DashboardForm initial={editingConfig}
                                                onClose={() => { setShowForm(false); setEditingConfig(null); }}
                                                onSaved={() => { setShowForm(false); setEditingConfig(null); load(); }} />}
      </AnimatePresence>
    </div>
  );
}

function RunDashboard({ did, onBack, onEdit, onDelete }) {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.post(`/analytics/dashboards/${did}/run`).then((r) => setPayload(r.data)).finally(() => setLoading(false));
  }, [did]);
  if (loading || !payload) return <Loading />;
  return (
    <div className="space-y-5" data-testid="run-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <button onClick={onBack} className="text-[10px] font-mono tracking-widest text-slate-400 hover:text-white flex items-center gap-1.5 mb-2">
            ← ALL DASHBOARDS
          </button>
          <h2 className="font-display text-2xl text-white">{payload.dashboard.name}</h2>
          {payload.dashboard.description && <p className="text-[12px] text-slate-400 mt-1">{payload.dashboard.description}</p>}
        </div>
        <div className="flex gap-2">
          <button onClick={onEdit} data-testid="run-dashboard-edit"
                  className="p-2 border border-white/10 text-slate-400 hover:border-[#D4AF37]/40 hover:text-[#D4AF37]">
            <Edit3 className="w-3 h-3" />
          </button>
          <button onClick={onDelete} data-testid="run-dashboard-delete" className="p-2 border border-white/10 text-slate-400 hover:border-red-500/40 hover:text-red-400">
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {payload.results.map((r, i) => {
          if (r.error) return (
            <div key={i} className="nexus-card p-4 border-red-500/30 text-sm text-red-400">
              {r.widget.title || "Widget"} · error: {r.error}
            </div>
          );
          const size = r.widget.size || "md";
          const colspan = size === "lg" ? "md:col-span-2 lg:col-span-2" : size === "sm" ? "" : "";
          return (
            <div key={i} className={`nexus-glass-panel p-5 ${colspan}`} data-testid={`widget-${i}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="label-eyebrow flex-1 truncate">{r.widget.title}</span>
              </div>
              <ChartBody result={r} compact />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DashboardForm({ initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [widgets, setWidgets] = useState(initial?.widgets || []);
  const [catalogue, setCatalogue] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/analytics/metrics").then((r) => setCatalogue(r.data)); }, []);

  const addWidget = (mid, label) => setWidgets((w) => [...w, { metric_id: mid, title: label, size: "md" }]);
  const removeWidget = (idx) => setWidgets((w) => w.filter((_, i) => i !== idx));
  const setSize = (idx, s) => setWidgets((w) => w.map((x, i) => i === idx ? { ...x, size: s } : x));

  const save = async () => {
    if (!name.trim()) return toast.error("Name required");
    if (widgets.length === 0) return toast.error("Add at least one widget");
    setBusy(true);
    try {
      const payload = { name, description, widgets };
      if (isEdit) await api.put(`/analytics/dashboards/${initial.id}`, payload);
      else await api.post("/analytics/dashboards", payload);
      toast.success(isEdit ? "Dashboard updated" : "Dashboard created");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return <Modal title={isEdit ? "Edit dashboard" : "New dashboard"} onClose={onClose} testid="dashboard-form" wide>
    <Field label="Name">
      <input value={name} onChange={(e) => setName(e.target.value)} data-testid="dashboard-name"
             className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" />
    </Field>
    <Field label="Description">
      <input value={description} onChange={(e) => setDescription(e.target.value)}
             className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" />
    </Field>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
      <div>
        <div className="label-eyebrow mb-2">// Available metrics</div>
        <div className="max-h-72 overflow-y-auto border border-white/10 p-2 space-y-2">
          {catalogue?.categories.map((c) => (
            <div key={c.name}>
              <div className="font-mono text-[10px] tracking-widest text-slate-500 mb-1">{c.name.toUpperCase()}</div>
              {c.metrics.map((m) => (
                <button key={m.id} onClick={() => addWidget(m.id, m.label)} data-testid={`add-widget-${m.id}`}
                        className="w-full text-left flex items-center gap-2 px-2 py-1.5 text-[12px] text-slate-300 hover:bg-white/5">
                  <Plus className="w-3 h-3 text-emerald-400" /> {m.label}
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="label-eyebrow mb-2">// Widgets in dashboard · {widgets.length}</div>
        <div className="max-h-72 overflow-y-auto border border-white/10 p-2 space-y-1.5">
          {widgets.length === 0 ? (
            <div className="text-[12px] text-slate-500 py-2 text-center">No widgets yet.</div>
          ) : widgets.map((w, i) => (
            <div key={i} className="flex items-center gap-2 p-2 bg-[#0A111F]/60 border border-white/5" data-testid={`widget-cfg-${i}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{w.title}</div>
              </div>
              <select value={w.size} onChange={(e) => setSize(i, e.target.value)}
                      className="bg-[#0A111F] border border-white/10 text-xs text-white px-1.5 py-0.5">
                <option value="sm">sm</option><option value="md">md</option><option value="lg">lg</option>
              </select>
              <button onClick={() => removeWidget(i)} className="p-1 text-slate-500 hover:text-red-400">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
    <SaveRow onSave={save} onClose={onClose} busy={busy} testid="dashboard-save" />
  </Modal>;
}

/* ─────────────────────────── Chart primitives ─────────────────────────── */
function ChartWithHeader({ result }) {
  return (
    <>
      <div className="flex items-center gap-2 mb-4">
        <span className="font-mono text-[10px] tracking-widest text-slate-500">{result.category?.toUpperCase()} · {result.chart?.toUpperCase()}</span>
      </div>
      <h3 className="font-display text-lg text-white mb-4">{result.label}</h3>
      <ChartBody result={result} />
    </>
  );
}

function ChartBody({ result, compact }) {
  const height = compact ? 200 : 300;
  if (result.kind === "kpi") {
    return (
      <div className="text-center py-6">
        <div className="font-display text-5xl text-white" data-testid="kpi-value">
          {typeof result.value === "number" ? (result.unit === "GHS" ? fmtCurrency(result.value) : result.value.toLocaleString()) : result.value}
          {result.unit === "%" && <span className="text-2xl text-[#D4AF37]">%</span>}
        </div>
        {result.unit && result.unit !== "%" && (
          <div className="font-mono text-[10px] tracking-widest text-slate-500 mt-2">{result.unit}</div>
        )}
        {result.sub && <div className="text-[12px] text-slate-400 mt-2 font-mono">{result.sub}</div>}
      </div>
    );
  }
  if (result.kind === "breakdown") {
    const data = result.breakdown || [];
    if (data.length === 0) return <Empty />;
    if (result.chart === "donut") {
      return (
        <ResponsiveContainer width="100%" height={height}>
          <RePie>
            <Pie data={data} dataKey="value" nameKey="label" innerRadius={compact ? 40 : 60} outerRadius={compact ? 75 : 105}
                 paddingAngle={2} stroke="none">
              {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
            </Pie>
            <Tooltip contentStyle={{ background: "#0A111F", border: "1px solid rgba(212,175,55,0.3)", fontSize: 12 }} />
          </RePie>
        </ResponsiveContainer>
      );
    }
    // bar
    return (
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
          <XAxis dataKey="label" stroke="#94A3B8" fontSize={10} interval={0} angle={data.length > 5 ? -25 : 0} textAnchor={data.length > 5 ? "end" : "middle"} height={data.length > 5 ? 60 : 30} />
          <YAxis stroke="#94A3B8" fontSize={10} />
          <Tooltip contentStyle={{ background: "#0A111F", border: "1px solid rgba(212,175,55,0.3)", fontSize: 12 }} />
          <Bar dataKey="value" fill="#D4AF37" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    );
  }
  if (result.kind === "series") {
    const data = result.series || [];
    if (data.length === 0) return <Empty />;
    return (
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
          <XAxis dataKey="label" stroke="#94A3B8" fontSize={10} />
          <YAxis stroke="#94A3B8" fontSize={10} />
          <Tooltip contentStyle={{ background: "#0A111F", border: "1px solid rgba(212,175,55,0.3)", fontSize: 12 }} />
          <Line type="monotone" dataKey="value" stroke="#D4AF37" strokeWidth={2} dot={{ fill: "#D4AF37", r: 3 }} />
        </LineChart>
      </ResponsiveContainer>
    );
  }
  return null;
}

/* ─────────────────────────── Shared ─────────────────────────── */
const fmtCurrency = (n) => {
  if (n == null) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
};

const Kpi = ({ label, value, accent }) => (
  <div className="nexus-card p-4 border-l-2" style={{ borderLeftColor: accent }}>
    <div className="label-eyebrow">{label}</div>
    <div className="font-display text-2xl mt-1 text-white">{value}</div>
  </div>
);

const Loading = () => (
  <div className="p-10 text-slate-400 font-mono text-xs flex items-center gap-2">
    <Loader2 className="w-3 h-3 animate-spin" /> LOADING…
  </div>
);

const Empty = () => (
  <div className="text-center py-12 text-sm text-slate-500">No data yet.</div>
);

const Field = ({ label, children }) => (
  <div className="mb-3">
    <label className="label-eyebrow block mb-1.5">{label}</label>
    {children}
  </div>
);

const SaveRow = ({ onSave, onClose, busy, testid, label = "SAVE" }) => (
  <div className="flex gap-2 mt-4">
    <button onClick={onClose} className="flex-1 text-[10px] font-mono tracking-widest py-2 border border-white/10 text-slate-300 hover:border-white/30">CANCEL</button>
    <button onClick={onSave} disabled={busy} data-testid={testid}
            className="flex-1 text-[10px] font-mono tracking-widest py-2 disabled:opacity-50"
            style={{ background: "var(--gold-500)", color: "#050B14" }}>
      {busy ? "SAVING…" : label}
    </button>
  </div>
);

const Modal = ({ title, onClose, children, testid, wide }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)" }} onClick={onClose} data-testid={testid}>
    <motion.div initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }}
                className={`nexus-glass-panel border border-white/10 p-6 w-full ${wide ? "max-w-3xl" : "max-w-md"} max-h-[90vh] overflow-y-auto`}
                onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl text-white">{title}</h2>
        <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
      </div>
      {children}
    </motion.div>
  </motion.div>
);
